<template>
    <b-modal id="editModal" body-class="px-0" ref="modal" title="Edit Commercial variant" v-model="show" @ok="handleSubmit" @hide="close"
             @shown="focusNameInput">

        <!-- Base Details -->
        <div class="card card-flat mb-0">
            <div class="bg-light">
                <h6 class="card-header text-secondary">Base Details</h6>
            </div>
            <div class="card-body">
                <div class="form-group">
                    <label>Commercial variant name</label>
                    <div class="position-relative">
                        <input type="text" ref="nameInput" name="name" v-validate="{required: true}" class="form-control" v-model="name">
                        <div class="invalid-feedback" v-if="errors.has('name')">{{ errors.first('name') }}</div>
                        <i class="form-group__bar"></i>
                    </div>
                </div>

                <div class="form-group">
                    <label>HSR number</label>
                    <div class="position-relative">
                        <input type="text" name="hsr_number" v-validate="{required: true}" class="form-control" v-model="hsr_number">
                        <div class="invalid-feedback" v-if="errors.has('hsr_number')">{{ errors.first('hsr_number') }}</div>
                        <i class="form-group__bar"></i>
                    </div>
                </div>

                <div class="form-group">
                    <label>UN number</label>
                    <div class="position-relative">
                        <input type="text" name="un_number" v-validate="{required: true}" class="form-control" v-model="un_number">
                        <div class="invalid-feedback" v-if="errors.has('un_number')">{{ errors.first('un_number') }}</div>
                        <i class="form-group__bar"></i>
                    </div>
                </div>

                <div class="form-group">
                    <label>Chemical State</label>
                    <div>
                        <select v-model="is_liquid" class="form-control">
                            <option :value="true">Liquid</option>
                            <option :value="false">Solid</option>
                        </select>
                    </div>
                </div>

                <div class="form-group">
                    <label>Classification</label>
                    <div class="position-relative">
                        <VueSelect
                                data-vv-as="classification"
                                label="name"
                                track-by="id"
                                v-validate="validation.classification"
                                v-model="classification"
                                data-vv-name="classification"
                                :options="classifications"
                                :allow-empty="false">
                        </VueSelect>
                        <div class="invalid-feedback" v-if="errors.has('classification')">{{ errors.first('classification') }}</div>
                        <i class="form-group__bar"></i>
                    </div>
                </div>
            </div>
        </div>

        <!-- Crop Types -->
        <div class="card card-flat mb-0">
            <div class="bg-light">
                <h6 class="card-header text-secondary" v-if="cropTypeId">Crop Type</h6>
                <h6 class="card-header text-secondary" v-else>Crop Types</h6>
            </div>
            <div class="card-body">
                <div class="form-group" v-if="!cropTypeId">
                    <b-table hover small class="no-borders" stacked="md" :items="cropTypeEntryRows" :fields="cropTypeEntriesTable.fields"
                             :show-empty="true">
                        <template slot="cropType" slot-scope="row">
                            <div class="position-relative">
                                <VueSelect
                                        data-vv-as="cropType"
                                        placeholder="CropType"
                                        label="name"
                                        track-by="id"
                                        v-b-tooltip="errors.first(`cropType_${row.index}`) || ''"
                                        v-validate="validation.entry_cropType"
                                        :value="row.item.cropType"
                                        :data-vv-name="`cropType_${row.index}`"
                                        :options="cropTypes"
                                        :allow-empty="false"
                                        :show-labels="false"
                                        @input="setCropTypeRow('cropType', row.index, $event)">
                                    <div slot="noResult" class="text-center">
                                        <small class="text-muted">No matches</small>
                                    </div>
                                </VueSelect>
                                <i class="form-group__bar"></i>
                            </div>
                        </template>
                        <template slot="is_usable" slot-scope="row">
                            <div class="position-relative">
                                <div class="custom-control custom-checkbox">

                                    <input class="custom-control-input"
                                           type="checkbox"
                                           v-model="row.item.is_usable"
                                    >
                                    <label class="custom-control-label">&nbsp;</label>
                                </div>
                                <i class="form-group__bar"></i>
                            </div>
                        </template>
                        <template slot="is_registered" slot-scope="row">
                            <div class="position-relative">
                                <div class="custom-control custom-checkbox">

                                    <input class="custom-control-input"
                                           type="checkbox"
                                           v-model="row.item.is_registered">
                                    <label class="custom-control-label">&nbsp;</label>
                                </div>
                                <i class="form-group__bar"></i>
                            </div>
                        </template>
                        <template slot="recommended_rate" slot-scope="row">
                            <div class="position-relative">
                                <VueMask
                                        class="form-control"
                                        placeholder="0..."
                                        v-b-tooltip="errors.first(`recommended_rate_${row.index}`) || ''"
                                        :value="row.item.recommended_rate"
                                        :class="{'is-invalid': errors.has(`recommended_rate_${row.index}`)}"
                                        :options="{numeral: true, min:0, max:1000, numeralThousandsGroupStyle: 'thousand'}"
                                        @input="setCropTypeRow('recommended_rate', row.index, $event)" required />
                                <i class="form-group__bar"></i>
                            </div>
                        </template>

                        <template slot="remove" slot-scope="row">
                            <b-button size="sm" variant="outline-danger" @click="removeCropTypeRow(row.index)">Remove</b-button>
                        </template>
                    </b-table>

                    <div class="mx-3">
                        <button type="button" class="btn btn-block btn-outline-success" @click.prevent="addCropTypeRow">
                            <i class="zmdi zmdi-plus"></i> Add another row
                        </button>
                    </div>
                </div>
                <template v-else>
                    <div class="form-group">
                        <label>Registered For Use</label>
                        <div>
                            <select v-model="crop_is_registered" class="form-control">
                                <option :value="true">Registered</option>
                                <option :value="false">Off Label</option>
                            </select>
                        </div>
                    </div>

                    <div class="form-group">
                        <label>Recommended Rate</label>
                        <div class="position-relative">
                            <VueMask
                                    class="form-control"
                                    placeholder="0..."
                                    v-model="crop_recommended_rate"
                                    data-vv-name="crop_recommended_rate"
                                    data-vv-as="Recommended rate"
                                    v-validate="validation.crop_recommended_rate"
                                    :class="{'is-invalid': errors.has(`crop_recommended_rate`)}"
                                    :options="{numeral: true, min:0, max:1000, numeralThousandsGroupStyle: 'thousand'}" />
                            <div class="invalid-feedback" v-if="errors.has('crop_recommended_rate')">{{ errors.first('crop_recommended_rate') }}</div>
                            <i class="form-group__bar"></i>
                        </div>
                    </div>
                </template>
            </div>
        </div>

        <!-- Active Ingredients -->
        <div class="card card-flat mb-0">
            <div class="bg-light">
                <h6 class="card-header text-secondary">Active Ingredients</h6>
            </div>
            <div class="card-body">
                <div class="form-group">
                    <b-table hover small class="no-borders" stacked="md" :items="activeIngredientEntryRows"
                             :fields="activeIngredientEntriesTable.fields"
                             :show-empty="true">
                        <template slot="chemical" slot-scope="row">
                            <div class="position-relative">
                                <VueSelect
                                        data-vv-as="chemical"
                                        placeholder="Chemical"
                                        label="name"
                                        track-by="id"
                                        v-b-tooltip="errors.first(`chemical_${row.index}`) || ''"
                                        v-validate="validation.entry_chemical"
                                        :value="row.item.chemical"
                                        :data-vv-name="`chemical_${row.index}`"
                                        :options="activeIngredients"
                                        :allow-empty="false"
                                        :show-labels="false"
                                        @input="setActiveIngredientRow('chemical', row.index, $event)">
                                    <div slot="noResult" class="text-center">
                                        <small class="text-muted">No matches</small>
                                    </div>
                                </VueSelect>
                                <i class="form-group__bar"></i>
                            </div>
                        </template>
                        <template slot="ingredient_percentage" slot-scope="row">
                            <div class="position-relative">
                                <VueMask
                                        class="form-control"
                                        placeholder="0..."
                                        v-b-tooltip="errors.first(`ingredient_percentage_${row.index}`) || ''"
                                        :value="row.item.ingredient_percentage"
                                        :class="{'is-invalid': errors.has(`ingredient_percentage_${row.index}`)}"
                                        :options="{numeral: true, min:0, max:1000, numeralThousandsGroupStyle: 'thousand'}"
                                        @input="setActiveIngredientRow('ingredient_percentage', row.index, $event)"
                                        v-validate="validation.ingredient_percentage"
                                        :data-vv-name="`ingredient_percentage_${row.index}`"
                                        required />
                                <i class="form-group__bar"></i>
                            </div>
                        </template>
                        <template slot="remove" slot-scope="row">
                            <b-button size="sm" variant="outline-danger" @click="removeActiveIngredientRow(row.index)">Remove</b-button>
                        </template>
                    </b-table>
                    <div class="mx-3">
                        <button type="button" class="btn btn-block btn-outline-success" @click.prevent="addActiveIngredientRow">
                            <i class="zmdi zmdi-plus"></i> Add another row
                        </button>
                    </div>
                </div>
            </div>
        </div>


        <template slot="modal-footer">
            <button class="btn btn-link text-danger mr-auto" @click="handleDelete" v-if="!commercialVariant.deleted_at">
                Disable
            </button>
            <button class="btn btn-link text-danger mr-auto" @click="handleRestore" v-if="commercialVariant.deleted_at">
                Restore
            </button>
            <button class="btn btn-secondary" @click="close">
                Cancel
            </button>
            <button class="btn btn-primary" :disabled="okDisabled" @click="handleSubmit">
                Save
            </button>
        </template>
    </b-modal>
</template>


<script>
import deleteChemicalCommercialVariantMutation from '@views/ChemicalCommercialVariants/EditModal/mutationDeleteChemicalCommercialVariant.graphql';
import restoreChemicalCommercialVariantMutation from '@views/ChemicalCommercialVariants/EditModal/mutationRestoreChemicalCommercialVariant.graphql';
import updateChemicalCommercialVariantMutation from '@views/ChemicalCommercialVariants/EditModal/mutationUpdateChemicalCommercialVariant.graphql';
import listActiveIngredientsQuery from '@views/ChemicalCommercialVariants/EditModal/queryListChemicalActiveIngredients.graphql';
import listCropTypesQuery from '@views/ChemicalCommercialVariants/EditModal/queryListCropTypes.graphql';

export default {
  name: 'edit-modal',
  apollo: {
    activeIngredients: {
      query: listActiveIngredientsQuery,
      update: data => data.filterChemicalActiveIngredients.data,
      variables: { input: { include_deleted: true } },
    },
    cropTypes: {
      query: listCropTypesQuery,
      update: data => data.filterCropTypes.data,
      variables: { input: { include_deleted: true } },
    },
  },
  props: {
    commercialVariant: {
      type: Object,
      required: true,
    },
    cropTypeId: {},
    classifications: {
      type: Array,
      required: true,
    },
    close: {
      type: Function,
      required: true,
    },
  },
  data() {
    let managedCropType;
    if (this.cropTypeId) {
      managedCropType = this.commercialVariant.cropTypes.find(link => link.cropType.id === this.cropTypeId);
    }

    return {
      name: this.commercialVariant.name,
      hsr_number: this.commercialVariant.hsr_number,
      un_number: this.commercialVariant.un_number,
      is_liquid: this.commercialVariant.is_liquid,
      crop_is_registered: managedCropType ? managedCropType.is_registered : false,
      crop_recommended_rate: managedCropType ? managedCropType.recommended_rate : null,
      activeIngredients: [],
      cropTypes: [],
      classification: this.classifications.find(classification => this.commercialVariant.classification.id === classification.id),
      cropTypeEntryRows: this.commercialVariant.cropTypes.map(data => ({
        cropType: data.cropType,
        is_usable: data.is_usable,
        is_registered: data.is_registered,
        recommended_rate: data.recommended_rate,
      })),
      activeIngredientEntryRows: this.commercialVariant.activeIngredients.map(data => ({
        chemical: data.activeIngredient,
        ingredient_percentage: data.ingredient_percentage,
      })),
      show: true,
      loading: 0,
      validation: {
        entry_chemical: { required: true },
        crop_recommended_rate: { required: true },
        ingredient_percentage: { required: true, min_value: 0 },
      },
      activeIngredientEntriesTable: {
        fields: [
          { key: 'chemical', label: 'Chemical' },
          { key: 'ingredient_percentage', label: 'Ingredient percentage' },
          { key: 'remove', label: 'Remove', 'class': 'text-right' },
        ],
      },
      cropTypeEntriesTable: {
        fields: [
          { key: 'cropType', label: 'CropType' },
          { key: 'is_usable', label: 'Is Usable' },
          { key: 'is_registered', label: 'Is Registered' },
          { key: 'recommended_rate', label: 'Recommended Rate' },
          { key: 'remove', label: 'Remove', 'class': 'text-right' },
        ],
      },
    };
  },
  computed: {
    okDisabled() {
      return this.name.length === 0;
    },
  },
  methods: {
    addActiveIngredientRow() {
      this.$validator.validateAll().then(validationWasSuccessful => {
        if (validationWasSuccessful) {
          this.activeIngredientEntryRows.push({
            chemical: null,
            ingredient_percentage: null,
          });
        }
      });
    },
    addCropTypeRow() {
      this.$validator.validateAll().then(validationWasSuccessful => {
        if (validationWasSuccessful) {
          this.cropTypeEntryRows.push({
            cropType: null,
            is_usable: false,
            is_registered: false,
            recommended_rate: null,
          });
        }
      });
    },
    removeCropTypeRow(index) {
      this.cropTypeEntryRows.splice(index, 1);
    },
    removeActiveIngredientRow(index) {
      this.activeIngredientEntryRows.splice(index, 1);
    },
    setCropTypeRow(field, index, value) {
      this.cropTypeEntryRows[index][field] = value;
    },
    setActiveIngredientRow(field, index, value) {
      this.activeIngredientEntryRows[index][field] = value;
    },
    handleSubmit() {
      if (this.okDisabled) {
        return false;
      }
      this.errors.remove('global');
      this.$validator.validateAll().then(validationWasSuccessful => {
        if (!validationWasSuccessful) {
          return;
        }

        const input = {
          id: this.commercialVariant.id,
          classification_id: this.classification.id,
          name: this.name,
          hsr_number: this.hsr_number,
          un_number: this.un_number,
          is_liquid: this.is_liquid,
        };

        input.active_ingredients = this.activeIngredientEntryRows.map(row => ({
          id: row.chemical.id,
          active_ingredient_percentage: row.ingredient_percentage,
        }));

        if (!this.cropTypeId) {
          input.crop_types = this.cropTypeEntryRows.map(row => ({
            id: row.cropType.id,
            is_usable: row.is_usable,
            is_registered: row.is_registered,
            recommended_rate: row.recommended_rate,
          }));
        } else {
          input.crop_recommended_rate = this.crop_recommended_rate;
          input.crop_is_registered = this.crop_is_registered;
        }

        this.$apollo
          .mutate({
            mutation: updateChemicalCommercialVariantMutation,
            variables: {
              input,
            },
          })
          .then(() => {
            this.$notifySuccess('Commercial variant updated!');
            this.close();
          })
          .catch(err => {
            this.$mapGraphQLError(err);
          })
          .catch(err => {
            console.error(err);
            this.$notifyFailure(
              'Unfortunately, we were unable to do that!',
            );
          });
      });
    },
    handleDelete() {
      this.$apollo
        .mutate({
          mutation: deleteChemicalCommercialVariantMutation,
          variables: {
            input: {
              id: this.commercialVariant.id,
            },
          },
        })
        .then(() => {
          this.$emit('refresh');
          this.$notifySuccess('Commercial variant disabled!');
          this.close();
        })
        .catch(err => {
          this.$mapGraphQLError(err);
        })
        .catch(err => {
          console.error(err);
          this.$notifyFailure(
            'Unfortunately, we were unable to do that!',
          );
        });
    },
    handleRestore() {
      this.$apollo
        .mutate({
          mutation: restoreChemicalCommercialVariantMutation,
          variables: {
            input: {
              id: this.commercialVariant.id,
            },
          },
          optimisticResponse: {
            __typename: 'Mutation',
            restoreChemicalCommercialVariant: {
              ...this.commercialVariant,
              deleted_at: null,
            },
          },
        })
        .then(() => {
          this.$emit('refresh');
          this.$notifySuccess('Commercial variant restored!');
          this.close();
        })
        .catch(err => {
          this.$mapGraphQLError(err);
        })
        .catch(err => {
          console.error(err);
          this.$notifyFailure(
            'Unfortunately, we were unable to do that!',
          );
        });
    },
    focusNameInput() {
      this.$refs.nameInput.focus();
    },
  },
};
</script>
